import { createSelector, createSlice } from '@reduxjs/toolkit';
import { shallowEqual } from 'react-redux';
import type { AppDispatch, RootState } from '../store';

import { DEFAULT_PAGE_SIZE } from '../constants';
import type { TripLeg } from '../models/reservation.model';
import type { PagedList } from '../models/paged-list.model';
import type { BaseEvent } from '../models/events/_BaseEvent';
import type { TripLegRequestV2 } from '../requests/trip-legs';

import { tripLegAdapter } from '../entities/trip-leg.entity';
import triplegService from '../../services/triplegService';
import * as uiSlice from '../ui.slice';

const DEFAULT_PAGED_LIST: PagedList<string[]> = {
  currentPage: 1,
  totalPages: 0,
  itemCount: 0,
  filters: {
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: 'pickUpDueTimeUTC',
    sortOrder: 'desc',
  },
  items: [],
};

type TripLegEntityState = {
  // The tripLegEventQueue is used to track incoming events so data fetches can
  // be debounced or the events processed in a specific order.
  tripLegEventQueue: Partial<BaseEvent>[];
  tripManagementTodaysTrips: PagedList<string[]>;
  loadingTripManagementTodaysTrips: boolean;
  loadedTripManagementTodaysTrips: boolean;
  tripManagementAllTrips: PagedList<string[]>;
  loadingTripManagementAllTrips: boolean;
  loadedTripManagementAllTrips: boolean;
  monitoringTodaysTrips: PagedList<string[]>;
  loadingMonitoringTodaysTrips: boolean;
  loadedMonitoringTodaysTrips: boolean;
  monitoringExceptions: PagedList<string[]>;
  loadingMonitoringExceptions: boolean;
  loadedMonitoringExceptions: boolean;
};

const tripLegSlice = createSlice({
  name: 'trip-leg',
  initialState: tripLegAdapter.getInitialState<TripLegEntityState>({
    tripLegEventQueue: [],
    tripManagementTodaysTrips: { ...DEFAULT_PAGED_LIST },
    loadingTripManagementTodaysTrips: false,
    loadedTripManagementTodaysTrips: false,
    tripManagementAllTrips: { ...DEFAULT_PAGED_LIST },
    loadingTripManagementAllTrips: false,
    loadedTripManagementAllTrips: false,
    monitoringTodaysTrips: { ...DEFAULT_PAGED_LIST },
    loadingMonitoringTodaysTrips: false,
    loadedMonitoringTodaysTrips: false,
    monitoringExceptions: { ...DEFAULT_PAGED_LIST },
    loadingMonitoringExceptions: false,
    loadedMonitoringExceptions: false,
  }),
  reducers: {
    setTripLegs: (state, action: { payload: PagedList<TripLeg> }) => {
      tripLegAdapter.setMany(state, action.payload.items as TripLeg[]);
    },
    tripLegOpened: (state, action: { payload: TripLeg }) => {
      tripLegAdapter.addOne(state, action.payload);
    },
    tripLegUpdated: (state, action: { payload: TripLeg }) => {
      tripLegAdapter.upsertOne(state, action.payload);
    },
    tripLegDriverAssigned: (state, action: { payload: TripLeg }) => {
      tripLegAdapter.upsertOne(state, action.payload);
    },
    tripLegExceptionCleared: (state, action: { payload: TripLeg }) => {
      tripLegAdapter.updateOne(state, {
        id: action.payload.tripLegReferenceID!,
        changes: {
          isException: false,
          tripLegExceptionBC: null,
        },
      });
    },
    addToTripLegEventQueue: (
      state,
      action: { payload: Partial<BaseEvent> },
    ) => {
      state.tripLegEventQueue = [...state.tripLegEventQueue, action.payload];
    },
    removeTripLegEventFromQueue: (
      state,
      action: { payload: Partial<BaseEvent> },
    ) => {
      const foundIndex = state.tripLegEventQueue.findIndex(
        (event) =>
          event.detailType === action.payload.detailType &&
          event.detail.tripLegReferenceID ===
            action.payload.detail.tripLegReferenceID,
      );
      if (foundIndex >= 0) {
        // Remove all previous events to the found index, since they didn't need to be processed
        state.tripLegEventQueue = state.tripLegEventQueue.slice(foundIndex + 1);
      }
    },
    setTripManagementTodaysTrips: (
      state,
      action: { payload: PagedList<TripLeg> },
    ) => {
      const newState = {
        ...state.tripManagementTodaysTrips,
        ...action.payload,
        items: action.payload.items.map(
          (item) => item.tripLegReferenceID as unknown as string[],
        ),
      };
      state.tripManagementTodaysTrips = newState;
    },
    setLoadingTripManagementTodaysTrips: (
      state,
      action: { payload: boolean },
    ) => {
      state.loadingTripManagementTodaysTrips = action.payload;
    },
    setLoadedTripManagementTodaysTrips: (
      state,
      action: { payload: boolean },
    ) => {
      state.loadedTripManagementTodaysTrips = action.payload;
    },
    setTripManagementAllTrips: (
      state,
      action: { payload: PagedList<TripLeg> },
    ) => {
      state.tripManagementAllTrips = {
        ...state.tripManagementAllTrips,
        ...action.payload,
        items: action.payload.items.map(
          (item) => item.tripLegReferenceID as unknown as string[],
        ),
      };
    },
    setLoadingTripManagementAllTrips: (state, action: { payload: boolean }) => {
      state.loadingTripManagementAllTrips = action.payload;
    },
    setLoadedTripManagementAllTrips: (state, action: { payload: boolean }) => {
      state.loadedTripManagementAllTrips = action.payload;
    },
    setMonitoringTodaysTrips: (
      state,
      action: { payload: PagedList<TripLeg> },
    ) => {
      state.monitoringTodaysTrips = {
        ...state.monitoringTodaysTrips,
        ...action.payload,
        items: action.payload.items.map(
          (item) => item.tripLegReferenceID as unknown as string[],
        ),
      };
    },
    setLoadingMonitoringTodaysTrips: (state, action: { payload: boolean }) => {
      state.loadingMonitoringTodaysTrips = action.payload;
    },
    setLoadedMonitoringTodaysTrips: (state, action: { payload: boolean }) => {
      state.loadedMonitoringTodaysTrips = action.payload;
    },
    setMonitoringExceptions: (
      state,
      action: { payload: PagedList<TripLeg> },
    ) => {
      state.monitoringExceptions = {
        ...state.monitoringExceptions,
        ...action.payload,
        items: action.payload.items.map(
          (item) => item.tripLegReferenceID as unknown as string[],
        ),
      };
    },
    setLoadingMonitoringExceptions: (state, action: { payload: boolean }) => {
      state.loadingMonitoringExceptions = action.payload;
    },
    setLoadedMonitoringExceptions: (state, action: { payload: boolean }) => {
      state.loadedMonitoringExceptions = action.payload;
    },
  },
});

const {
  setTripLegs,
  tripLegOpened,
  tripLegUpdated,
  tripLegDriverAssigned,
  tripLegExceptionCleared,
  addToTripLegEventQueue,
  removeTripLegEventFromQueue,
  setTripManagementTodaysTrips,
  setLoadingTripManagementTodaysTrips,
  setLoadedTripManagementTodaysTrips,
  setTripManagementAllTrips,
  setLoadingTripManagementAllTrips,
  setLoadedTripManagementAllTrips,
  setLoadingMonitoringTodaysTrips,
  setMonitoringTodaysTrips,
  setLoadedMonitoringTodaysTrips,
  setMonitoringExceptions,
  setLoadingMonitoringExceptions,
  setLoadedMonitoringExceptions,
} = tripLegSlice.actions;

const fetchTripManagementTodaysTripsAsync = (request: TripLegRequestV2) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setLoadingTripManagementTodaysTrips(true));
    dispatch(setLoadedTripManagementTodaysTrips(false));
    triplegService.getTripLegsV2(request).subscribe({
      next: (response) => {
        dispatch(setTripLegs(response as unknown as PagedList<TripLeg>));
        dispatch(
          setTripManagementTodaysTrips(
            response as unknown as PagedList<TripLeg>,
          ),
        );
        dispatch(setLoadingTripManagementTodaysTrips(false));
        dispatch(setLoadedTripManagementTodaysTrips(true));
      },
      error: (error) => {
        dispatch(uiSlice.setErrorMessage(error.message));
        dispatch(setLoadingTripManagementTodaysTrips(false));
        dispatch(setLoadedTripManagementTodaysTrips(false));
      },
    });
  };
};

const fetchTripManagementAllTripsAsync = (request: TripLegRequestV2) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setLoadingTripManagementAllTrips(true));
    dispatch(setLoadedTripManagementAllTrips(false));
    triplegService.getTripLegsV2(request).subscribe({
      next: (response) => {
        dispatch(setTripLegs(response as unknown as PagedList<TripLeg>));
        dispatch(
          setTripManagementAllTrips(response as unknown as PagedList<TripLeg>),
        );
        dispatch(setLoadingTripManagementAllTrips(false));
        dispatch(setLoadedTripManagementAllTrips(true));
      },
      error: (error) => {
        dispatch(uiSlice.setErrorMessage(error.message));
        dispatch(setLoadingTripManagementAllTrips(false));
        dispatch(setLoadedTripManagementAllTrips(false));
      },
    });
  };
};

/*
 * Selectors
 * All necessary selectors for filtering and sorting trip legs for various
 * pages, including trip management and monitoring.
 */

const tripLegSelectors = tripLegAdapter.getSelectors<RootState>(
  (state) => state.tripLegV2,
);

const selectAllTripLegs = (state: RootState) =>
  tripLegSelectors.selectAll(state);

const selectTripLegEventQueue = (state: RootState) =>
  state.tripLegV2.tripLegEventQueue;

// Selectors for loading and loaded boolean states
const selectLoadingTripManagementTodaysTrips = (state: RootState) =>
  state.tripLegV2.loadingTripManagementTodaysTrips;
const selectLoadedTripManagementTodaysTrips = (state: RootState) =>
  state.tripLegV2.loadedTripManagementTodaysTrips;
const selectLoadingTripManagementAllTrips = (state: RootState) =>
  state.tripLegV2.loadingTripManagementAllTrips;
const selectLoadedTripManagementAllTrips = (state: RootState) =>
  state.tripLegV2.loadedTripManagementAllTrips;
const selectLoadingMonitoringTodaysTrips = (state: RootState) =>
  state.tripLegV2.loadingMonitoringTodaysTrips;
const selectLoadedMonitoringTodaysTrips = (state: RootState) =>
  state.tripLegV2.loadedMonitoringTodaysTrips;
const selectLoadingMonitoringExceptions = (state: RootState) =>
  state.tripLegV2.loadingMonitoringExceptions;
const selectLoadedMonitoringExceptions = (state: RootState) =>
  state.tripLegV2.loadedMonitoringExceptions;

// Selectors for each view's state
const selectTripManagementTodaysTripsState = (state: RootState) =>
  state.tripLegV2.tripManagementTodaysTrips;
const selectTripManagementAllTripsState = (state: RootState) =>
  state.tripLegV2.tripManagementAllTrips;
const selectMonitoringTodaysTripsState = (state: RootState) =>
  state.tripLegV2.monitoringTodaysTrips;
const selectMonitoringExceptionsState = (state: RootState) =>
  state.tripLegV2.monitoringExceptions;

// Selectors for IDs retrieved from API requests
const selectTripManagementTodaysTripsIDs = createSelector(
  [selectAllTripLegs, selectTripManagementTodaysTripsState],
  (entities, tripManagementTodaysTripsState) => {
    const ids = entities.map((entity: TripLeg) => entity.tripLegReferenceID);
    return tripManagementTodaysTripsState.items.filter((id) =>
      ids.includes(id as unknown as string),
    );
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectTripManagementAllTripsIDs = createSelector(
  [selectAllTripLegs, selectTripManagementAllTripsState],
  (entities, tripManagementAllTripsState) => {
    const ids = entities.map((entity: TripLeg) => entity.tripLegReferenceID);
    return tripManagementAllTripsState.items.filter((id) =>
      ids.includes(id as unknown as string),
    );
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectMonitoringTodaysTripsIDs = createSelector(
  [selectAllTripLegs, selectMonitoringTodaysTripsState],
  (entities, monitoringTodaysTripsState) => {
    const ids = entities.map((entity: TripLeg) => entity.tripLegReferenceID);
    return monitoringTodaysTripsState.items.filter((id) =>
      ids.includes(id as unknown as string),
    );
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectMonitoringExceptionsIDs = createSelector(
  [selectAllTripLegs, selectMonitoringExceptionsState],
  (entities, monitoringExceptionsState) => {
    const ids = entities.map((entity: TripLeg) => entity.tripLegReferenceID);
    return monitoringExceptionsState.items.filter((id) =>
      ids.includes(id as unknown as string),
    );
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

// Derived selectors for entities mapped to IDs retrieved from API requests,
// These are what should be used in the view
const selectTripManagementTodaysTrips = createSelector(
  [
    selectAllTripLegs,
    selectTripManagementTodaysTripsIDs,
    selectTripManagementTodaysTripsState,
  ],
  (entities, tripManagementTodaysTripsIDs, tripManagementTodaysTripsState) => {
    return {
      currentPage: tripManagementTodaysTripsState.currentPage,
      totalPages: tripManagementTodaysTripsState.totalPages,
      itemCount: tripManagementTodaysTripsState.itemCount,
      filters: tripManagementTodaysTripsState.filters,
      items: entities
        .filter((entity: TripLeg) =>
          tripManagementTodaysTripsIDs.includes(
            entity.tripLegReferenceID as unknown as string[],
          ),
        )
        .sort(
          (a, b) =>
            tripManagementTodaysTripsState.items.indexOf(
              a.tripLegReferenceID as unknown as string[],
            ) -
            tripManagementTodaysTripsState.items.indexOf(
              b.tripLegReferenceID as unknown as string[],
            ),
        ),
    };
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectTripManagementAllTrips = createSelector(
  [
    selectAllTripLegs,
    selectTripManagementAllTripsIDs,
    selectTripManagementAllTripsState,
  ],
  (entities, tripManagementAllTripsIDs, tripManagementAllTripsState) => {
    return {
      currentPage: tripManagementAllTripsState.currentPage,
      totalPages: tripManagementAllTripsState.totalPages,
      itemCount: tripManagementAllTripsState.itemCount,
      filters: tripManagementAllTripsState.filters,
      items: entities
        .filter((entity: TripLeg) =>
          tripManagementAllTripsIDs.includes(
            entity.tripLegReferenceID as unknown as string[],
          ),
        )
        .sort(
          (a, b) =>
            tripManagementAllTripsState.items.indexOf(
              a.tripLegReferenceID as unknown as string[],
            ) -
            tripManagementAllTripsState.items.indexOf(
              b.tripLegReferenceID as unknown as string[],
            ),
        ),
    };
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectMonitoringTodaysTrips = createSelector(
  [
    selectAllTripLegs,
    selectMonitoringTodaysTripsIDs,
    selectMonitoringTodaysTripsState,
  ],
  (entities, monitoringTodaysTripsIDs, monitoringTodaysTripsState) => {
    return {
      currentPage: monitoringTodaysTripsState.currentPage,
      totalPages: monitoringTodaysTripsState.totalPages,
      itemCount: monitoringTodaysTripsState.itemCount,
      filters: monitoringTodaysTripsState.filters,
      items: entities
        .filter((entity: TripLeg) =>
          monitoringTodaysTripsIDs.includes(
            entity.tripLegReferenceID as unknown as string[],
          ),
        )
        .sort(
          (a, b) =>
            monitoringTodaysTripsState.items.indexOf(
              a.tripLegReferenceID as unknown as string[],
            ) -
            monitoringTodaysTripsState.items.indexOf(
              b.tripLegReferenceID as unknown as string[],
            ),
        ),
    };
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectMonitoringExceptions = createSelector(
  [
    selectAllTripLegs,
    selectMonitoringExceptionsIDs,
    selectMonitoringExceptionsState,
  ],
  (entities, monitoringExceptionsIDs, monitoringExceptionsState) => {
    return {
      currentPage: monitoringExceptionsState.currentPage,
      totalPages: monitoringExceptionsState.totalPages,
      itemCount: monitoringExceptionsState.itemCount,
      filters: monitoringExceptionsState.filters,
      items: entities
        .filter((entity: TripLeg) =>
          monitoringExceptionsIDs.includes(
            entity.tripLegReferenceID as unknown as string[],
          ),
        )
        .sort(
          (a, b) =>
            monitoringExceptionsState.items.indexOf(
              a.tripLegReferenceID as unknown as string[],
            ) -
            monitoringExceptionsState.items.indexOf(
              b.tripLegReferenceID as unknown as string[],
            ),
        ),
    };
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

export {
  tripLegSlice,
  tripLegOpened,
  tripLegUpdated,
  tripLegDriverAssigned,
  tripLegExceptionCleared,
  addToTripLegEventQueue,
  removeTripLegEventFromQueue,
  fetchTripManagementTodaysTripsAsync,
  selectAllTripLegs,
  selectTripLegEventQueue,
  selectTripManagementTodaysTrips,
  selectTripManagementAllTrips,
  selectMonitoringTodaysTrips,
  selectMonitoringExceptions,
  selectLoadingTripManagementTodaysTrips,
  selectLoadedTripManagementTodaysTrips,
  selectLoadingTripManagementAllTrips,
  selectLoadedTripManagementAllTrips,
  selectLoadingMonitoringTodaysTrips,
  selectLoadedMonitoringTodaysTrips,
  selectLoadingMonitoringExceptions,
  selectLoadedMonitoringExceptions,
};
